/**
 * Criaturas animadas (F50 e "mais criaturas animadas", ADR 0012): as que ganham modelo com skin e
 * clipes, gerados sem trabalho manual pelo build dos modelos. Os esqueletos e as bruxas vêm dos
 * personagens KayKit (Skeletons 1.1 e Adventurers 2.0, CC0), no Rig_Medium dos avatares; Pesadelo,
 * Harpia, Lobisomem, Quimera, Cervo e Dragão, dos moldes do animador (cavalo, ave, raposa e dragão),
 * pesados pelo `autoRig`; Troll, Ciclope e Guardiões, das peças do boneco procedural de hoje presas
 * rígidas ao esqueleto do molde humano. Javali e Ouriço continuam procedurais (ADR 0007). Só visual:
 * ids, corpos, servidor, protocolo e regras não mudam. Os inimigos maiores da F89 (ADR 0019) são
 * versões grandes de inimigos de antes, com peças do kit do Blender (`peca`).
 */

/** Molde do animador (o rig e o modelo de referência dele) de uma criatura que não vem do KayKit. */
export type CreatureTemplate = "cavalo" | "ave" | "raposa" | "dragao";

/**
 * De onde vem a malha: um personagem KayKit (o nome do arquivo, sem `.glb`), um molde do animador ou
 * as peças do boneco procedural (`rigido`: o construtor do tipo `shape` em `creature-shapes.ts` do
 * cliente), cada uma presa com peso 1 a um osso do molde humano posto nos pivôs dele.
 */
export type CreatureSource =
  | { readonly kind: "kaykit"; readonly model: string }
  | { readonly kind: "molde"; readonly template: CreatureTemplate }
  | { readonly kind: "rigido"; readonly shape: string };

/** Uma variante da malha: a fonte e as peças dela que saem (o elmo do Rei vira coroa). */
export interface CreatureVariant {
  /** Id da variante (pt-BR): vai no nome do arquivo e no `stats()`. */
  readonly id: string;
  readonly source: CreatureSource;
  /** Peças do arquivo que não entram (nomes dos nós). */
  readonly drop?: readonly string[];
}

/**
 * Recoloração no build: as células do atlas da fonte (KayKit 8 × 4, moldes 8 × 8, em ordem de leitura)
 * passam à cor `hex` (sRGB), mantendo o claro e o escuro de cada canto (a conta da tinta dos itens).
 * Sem `cells`, a malha inteira.
 */
export interface CreatureTint {
  readonly cells?: readonly number[];
  readonly hex: string;
}

/** Peças do kit modelado no Blender (F89, `tools/assets/blender/inimigos.py`). */
export type CreaturePiece = "ombreira" | "espinhos" | "elmo-chifrudo" | "cristais";

/**
 * Peça presa rígida a ossos (peso 1), gerada no build: a coroa sobre o osso `bones[0]` (no topo dos
 * vértices dele, ou em volta das células `cells` da peça `around`: a faixa do chapéu), labaredas
 * (`chamas`) ou tufos de pelo (`crina`) no alto de cada osso de `bones`, um par de chifres curvos
 * (`chifres`) ou de galhadas (`galhada`) no alto da cabeça (`bones[0]`) e, na F89, uma peça do kit do
 * Blender (`peca`, a `piece`) em cada osso de `bones`: no ponto mais alto, mais atrás ou mais à frente
 * dos vértices dele, ou no alto da ponta do osso mais perto do meio do corpo (`ombro`: o braço está em T
 * na pose de ligação) (`at`), virada para cima, para a frente ou para trás (`face`), espelhada no lado
 * esquerdo. `glow`: malha à parte com brilho próprio (só a neblina age nela), como os olhos e o fogo dos
 * bonecos procedurais.
 */
export interface CreatureAttachment {
  readonly kind: "coroa" | "chamas" | "crina" | "chifres" | "galhada" | "peca";
  /** Só nas `peca`: a peça do kit, onde ela encaixa no osso e para onde aponta o alto dela. */
  readonly piece?: CreaturePiece;
  readonly at?: "topo" | "costas" | "frente" | "ombro";
  readonly face?: "cima" | "frente" | "tras";
  readonly bones: readonly string[];
  /** Cor (sRGB) e, nas chamas, a da ponta. */
  readonly hex: string;
  readonly tip?: string;
  /** Tamanho (m): a altura das pontas da coroa, a da labareda ou do tufo, o comprimento do chifre ou da galhada, a altura da peça do kit. */
  readonly size: number;
  readonly around?: string;
  readonly cells?: readonly number[];
  readonly glow?: boolean;
}

export interface CreatureLook {
  /** Id da criatura (`CreatureType.id`). */
  readonly creature: string;
  /** Escolhida pelo id da criatura na partida (`creatureVariant`). */
  readonly variants: readonly CreatureVariant[];
  /**
   * Altura parada no início de `parado`, em metros: nos KayKit, a do manequim do Rig_Medium (1,8 = a do
   * jogador; as peças e chapéus passam dela); nos moldes, a do modelo. Nas peças rígidas, a do boneco
   * procedural parado no primeiro quadro do `parado` (o build não muda a escala dele: só confere, a 2 %).
   */
  readonly height: number;
  readonly tint?: readonly CreatureTint[];
  readonly attachments?: readonly CreatureAttachment[];
}

const SKELETONS = (model: string) => ({ kind: "kaykit", model }) as const;
const RIGID = (shape: string) => ({ kind: "rigido", shape }) as const;

/**
 * Células do atlas do cavalo do animador (8 × 8): 57 o corpo, 6 a crina e a cauda, 56 as canelas, 51
 * os cascos, 59 o focinho e 16 os olhos.
 */
const HORSE = { body: 57, mane: 6, shins: 56, hooves: 51, muzzle: 59, eyes: 16 } as const;

/** Célula dos olhos da raposa do animador (8 × 8). */
const FOX_EYES = 29;

/** Guardião: as peças do boneco de pedra, a cor do santuário vem do construtor de cada tipo. */
const guardian = (color: string, height: number): CreatureLook => ({ creature: `guardiao-${color}`, variants: [{ id: "guardiao", source: RIGID(`guardiao-${color}`) }], height });

/** Pele cinza-esverdeada das bruxas (a dos bonecos procedurais). */
const WITCH_SKIN = "#A7B597";

/**
 * Criaturas animadas e o visual de cada uma (decisão do dono, 2026-10-06). As células são as do atlas
 * da fonte: no Mago KayKit, 0 é a pele do rosto e 23 a das mãos, 8 o manto e as mangas, 9 o chapéu, 10 a
 * capa, 1 o cabelo e 2 os olhos e a boca.
 */
export const CREATURE_LOOKS: readonly CreatureLook[] = [
  {
    // Esqueleto: os esqueletos KayKit, um por id (lacaio de capuz, ladino de capa e mago de chapéu).
    creature: "corpo-seco",
    variants: [
      { id: "lacaio", source: SKELETONS("Skeleton_Minion") },
      { id: "ladino", source: SKELETONS("Skeleton_Rogue") },
      { id: "mago", source: SKELETONS("Skeleton_Mage") },
    ],
    height: 1.8,
  },
  {
    // Rei Esqueleto: o guerreiro KayKit 1,38× maior, sem o elmo e de coroa de ouro.
    creature: "corpo-seco-anciao",
    variants: [{ id: "guerreiro", source: SKELETONS("Skeleton_Warrior"), drop: ["Skeleton_Warrior_Helmet"] }],
    height: 1.8 * 1.38,
    attachments: [{ kind: "coroa", bones: ["head"], hex: "#E2B23A", size: 0.16 }],
  },
  {
    // Bruxa: o Mago KayKit de pele verde e manto e chapéu de musgo escuro (não lê como o Mago roxo do jogador).
    creature: "cuca",
    variants: [{ id: "bruxa", source: { kind: "kaykit", model: "Mage" } }],
    height: 1.8,
    tint: [
      { cells: [0, 23], hex: WITCH_SKIN },
      { cells: [8, 9], hex: "#38432E" },
      { cells: [10], hex: "#4A3B2A" },
    ],
  },
  {
    // Rainha-Bruxa: a Bruxa maior, de manto vinho, com a coroa de ouro na base do chapéu.
    creature: "cuca-rainha",
    variants: [{ id: "rainha", source: { kind: "kaykit", model: "Mage" } }],
    height: 1.8 * 1.3,
    tint: [
      { cells: [0, 23], hex: WITCH_SKIN },
      { cells: [8, 9], hex: "#4A1F3A" },
      { cells: [10], hex: "#2A2233" },
      // a faixa do chapéu e o cinto, de ouro como a coroa
      { cells: [5], hex: "#E2B23A" },
    ],
    // A coroa em volta da faixa do chapéu (a célula 5 do atlas), com pontas altas que passam da aba.
    attachments: [{ kind: "coroa", bones: ["head"], hex: "#E2B23A", size: 0.3, around: "Mage_Hat", cells: [5] }],
  },
  {
    // Pesadelo: o cavalo do molde, negro, com a crina e a cauda de fogo.
    creature: "mula-sem-cabeca",
    variants: [{ id: "cavalo", source: { kind: "molde", template: "cavalo" } }],
    height: 1.9,
    tint: [{ hex: "#221C22" }],
    attachments: [{ kind: "chamas", bones: ["spine_5", "spine_6", "head", "tail_2", "tail_3"], hex: "#FF7A1A", tip: "#FFD25A", size: 0.34, glow: true }],
  },
  {
    // Harpia: a ave do molde, escurecida, de bico claro.
    creature: "matinta-pereira",
    variants: [{ id: "ave", source: { kind: "molde", template: "ave" } }],
    height: 0.8,
    tint: [{ hex: "#2B2733" }],
  },
  {
    // Lobisomem: a raposa do molde no tamanho do corpo do boneco de hoje, um pouco maior (1,3 m na cabeça:
    // ~1,85 m do focinho à anca, contra 1,7 m do boneco procedural, e ~2,9 m até a ponta da cauda; a
    // 1,9 m ela teria 4 m), escura, de olhos vermelhos e crina de tufos no pescoço.
    creature: "lobisomem",
    variants: [{ id: "lobo", source: { kind: "molde", template: "raposa" } }],
    height: 1.3,
    tint: [{ hex: "#3B3A40" }, { cells: [FOX_EYES], hex: "#E8202A" }],
    attachments: [{ kind: "crina", bones: ["Spine_2001", "Spine_3", "Spine_4"], hex: "#24232A", tip: "#4A4950", size: 0.26 }],
  },
  {
    // Quimera: o cavalo do molde enorme e escuro, de chifres curvos e olhos de fogo.
    creature: "cabra-cabriola",
    variants: [{ id: "quimera", source: { kind: "molde", template: "cavalo" } }],
    height: 2.0,
    tint: [
      { cells: [HORSE.body], hex: "#2F2A26" },
      { cells: [HORSE.mane, HORSE.shins, HORSE.hooves], hex: "#14110F" },
      { cells: [HORSE.muzzle], hex: "#3A332E" },
      { cells: [HORSE.eyes], hex: "#FF6A1A" },
    ],
    attachments: [{ kind: "chifres", bones: ["head"], hex: "#CDBB95", size: 0.55 }],
  },
  {
    // Cervo: o cavalo do molde pequeno, pardo, de focinho claro e galhada.
    creature: "capivara",
    variants: [{ id: "cervo", source: { kind: "molde", template: "cavalo" } }],
    height: 1.05,
    tint: [
      { cells: [HORSE.body], hex: "#8A5A3C" },
      { cells: [HORSE.mane, HORSE.shins], hex: "#5C3A26" },
      { cells: [HORSE.hooves], hex: "#2E2620" },
      { cells: [HORSE.muzzle], hex: "#E8DCC0" },
    ],
    attachments: [{ kind: "galhada", bones: ["head"], hex: "#D9C9A8", size: 0.42 }],
  },
  // Troll e Ciclope: as peças dos bonecos de hoje (pelo, presas, o olho aceso), presas aos ossos. O Troll
  // parado é curvado (2,47 m de pé).
  { creature: "capelobo", variants: [{ id: "troll", source: RIGID("capelobo") }], height: 2.12 },
  { creature: "mapinguari", variants: [{ id: "ciclope", source: RIGID("mapinguari") }], height: 3.55 },
  // Guardiões: um boneco de pedra (a mesma receita), cinco cores; as cópias da Rosa usam o mesmo modelo.
  guardian("verde", 2.71),
  guardian("azul", 2.71),
  guardian("vermelho", 2.71),
  guardian("amarelo", 2.71),
  guardian("rosa", 2.71),
  // Inimigos maiores (F89): o corpo de um inimigo de antes, maior, com peças do kit modelado no Blender
  // (`tools/assets/blender/inimigos.py`) presas aos ossos.
  {
    // Lobisomem Alfa: a raposa do molde 1,55× o Lobisomem (~2,9 m do focinho à anca), quase negra, de olhos
    // vermelhos, crina mais alta no pescoço e uma fileira de espinhos no meio do dorso.
    creature: "lobisomem-alfa",
    variants: [{ id: "lobo", source: { kind: "molde", template: "raposa" } }],
    height: 1.3 * 1.55,
    tint: [{ hex: "#2A2930" }, { cells: [FOX_EYES], hex: "#E8202A" }],
    attachments: [
      { kind: "crina", bones: ["Spine_2001", "Spine_3"], hex: "#1C1B21", tip: "#4A4950", size: 0.34 },
      { kind: "peca", piece: "espinhos", bones: ["Spine_2"], at: "costas", hex: "#3A3840", tip: "#D9D2C2", size: 0.5 },
    ],
  },
  {
    // Esqueleto Colosso: o guerreiro KayKit 1,75× o Esqueleto (3,15 m), sem o elmo do pack, com o elmo de
    // chifres, ombreiras com espinhos e a fileira de espinhos nas costas.
    creature: "corpo-seco-colosso",
    variants: [{ id: "colosso", source: SKELETONS("Skeleton_Warrior"), drop: ["Skeleton_Warrior_Helmet"] }],
    height: 1.8 * 1.75,
    attachments: [
      { kind: "peca", piece: "elmo-chifrudo", bones: ["head"], hex: "#4A4A52", tip: "#D8D2C0", size: 0.42 },
      { kind: "peca", piece: "ombreira", bones: ["upperarml", "upperarmr"], at: "ombro", hex: "#4A4A52", tip: "#D8D2C0", size: 0.55 },
      { kind: "peca", piece: "espinhos", bones: ["chest"], at: "costas", hex: "#4A4A52", tip: "#D8D2C0", size: 0.6 },
    ],
  },
  {
    // Troll de Pedra: o boneco do Troll 1,35× maior (`trollBuilder` em `creature-shapes.ts`), de pele de
    // pedra, com cristais acesos nas costas (como os dos Guardiões, na malha acesa dos olhos) e ombreiras de pedra.
    creature: "capelobo-ancestral",
    variants: [{ id: "troll", source: RIGID("capelobo-ancestral") }],
    height: 2.12 * 1.35,
    attachments: [
      { kind: "peca", piece: "cristais", bones: ["spine_02"], at: "costas", face: "tras", hex: "#5C6E7A", tip: "#B8E4FF", size: 0.8, glow: true },
      { kind: "peca", piece: "ombreira", bones: ["upperarm_l", "upperarm_r"], at: "ombro", hex: "#55524D", tip: "#8A857C", size: 0.55 },
    ],
  },
  {
    // Dragão: o dragão do molde (negro, de asas carmim), com labaredas acesas no dorso e na cauda.
    creature: "boitata",
    variants: [{ id: "dragao", source: { kind: "molde", template: "dragao" } }],
    height: 2.6,
    attachments: [{ kind: "chamas", bones: ["spine_2", "spine_3", "spine_4", "tail_1", "tail_2", "tail_3"], hex: "#FF6A1A", tip: "#FFD25A", size: 0.7, glow: true }],
  },
];

export const CREATURE_LOOK_BY_ID: ReadonlyMap<string, CreatureLook> = new Map(CREATURE_LOOKS.map((look) => [look.creature, look]));

/** O visual animado da criatura `type`, se ela tiver um. */
export function creatureLook(type: string): CreatureLook | undefined {
  return CREATURE_LOOK_BY_ID.get(type);
}

/**
 * Variante da criatura de id `id` (o id da entidade na partida): um hash estável do texto (FNV-1a), o
 * mesmo em todo cliente e a cada vez que ela aparece. Sem variantes, `undefined`.
 */
export function creatureVariant(look: CreatureLook, id: string): CreatureVariant | undefined {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return look.variants[hash % look.variants.length];
}
